import Vue from 'vue'

/**
 * Gift card issuance against the gift-card subdomain (Shift4/Givex `dc_904`
 * Register), a different Lambda on its OWN gateway, mapped at base path `gift-cards`
 * on api[-test].ovationup.com. It moved off the shared `app-services` gateway when the
 * subdomain took ownership of its RestApi; the old
 * `app-services/gift-cards/v1/gift-cards` path is retired, not aliased.
 * Issuance no longer goes through `send-reply`: the old Moneris flow attached
 * the card to the reply message, but this service owns the whole transaction
 * and texts the guest itself — threading it through send-reply would double-text them.
 */
export default {
  /**
   * `companyId`/`locationId` go up as headers, not in the body. The ADR-003
   * authorizer itself only format-validates them (24-hex ObjectId) — it does not
   * check the caller's grants. The gift-card endpoint's own controller closes that
   * gap independently, resolving the caller's real authorized company/locations and
   * rejecting an asserted id outside them before touching Shift4. `locationId` also
   * picks the per-location Shift4/Givex merchant that funds the card, so it must be
   * the conversation's own location regardless of that check.
   */
  issue({
    amountCents,
    customerId,
    guestContact,
    companyId,
    locationId,
    idempotencyKey,
    confirmDuplicate
  }) {
    return Vue.http.post(
      'gift-cards/v1',
      {
        amountCents,
        // Required. The gift-card subdomain owns only its own collection, so it never
        // find-or-creates a customer any more — the conversation already holds the guest,
        // and the card is filed against the id sent here. `guestContact` stays: the
        // backend guest-scopes its rate-limit and idempotency claims on the phone.
        customerId,
        guestContact
      },
      {
        headers: {
          'X-Company-Id': companyId,
          'X-Location-Id': locationId,
          // Sent so the endpoint can dedupe retries once it supports one — a no-op
          // header if it doesn't yet.
          ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          // Opt-out of the backend's duplicate-issue guard. Sent ONLY when the operator
          // has been shown the existing card and confirmed a second one — the header is
          // omitted otherwise rather than sent as 'false', so a bug that drops the flag
          // fails toward blocking a duplicate rather than funding one.
          ...(confirmDuplicate ? { 'X-Confirm-Duplicate': 'true' } : {})
        }
      }
    )
  }
}
